import type { TOC } from '@ember/component/template-only';
import { or, and } from 'tio-ui/utilities';
import TableSortableHeader, { type SortFormat } from './table-sortable-header';

// Helper to create an array of a specific length for skeleton rows
const createSkeletonArray = (length: number) => Array.from({ length }, (_, i) => i);

/**
 * Signature for the THeadTr component.
 * This component represents a table header row (`<tr>`) with specific styling.
 *
 * @block default - The default block for yielding content inside the row.
 */
interface TheadTrSignature {
  Blocks: {
    default: [];
  };
  Element: HTMLTableRowElement;
}

/**
 * Table Header Row Component
 *
 * This component is a table header row (`<tr>`) with specific styling.
 *
 * @example
 * ```gts
 * <THeadTr>
 *   <th>Header 1</th>
 *   <th>Header 2</th>
 * </THeadTr>
 * ```
 */
const THeadTr: TOC<TheadTrSignature> = <template>
  <tr ...attributes>
    {{yield}}
  </tr>
</template>;

/**
 * Signature for the THeadTh component.
 * This component represents a table header cell (`<th>`) with sorting capabilities.
 *
 * @arg {string} [sortField] - The field to sort by when the header is clicked.
 * @arg {string} [activeSortField] - The currently active sort field.
 * @arg {(sortField: string) => void} [onSort] - Callback to trigger sorting.
 * @arg {SortFormat} [sortFormat] - The format of the sort string ('prefix' for -field or 'suffix' for field:desc).
 *
 * @block default - The default block for yielding content inside the cell.
 */
interface THeadThSignature {
  Args: {
    sortField?: string;
    activeSortField?: string;
    onSort?: (sortField: string) => void;
    /**
     * Controls the format of the sort string passed to onSort:
     * - 'prefix' (default): Uses "-fieldName" for descending (Rails/JSON:API convention)
     * - 'suffix': Uses "fieldName:desc" for descending (OpenSearch convention)
     */
    sortFormat?: SortFormat;
  };
  Blocks: {
    default: [];
  };
  Element: HTMLTableCellElement;
}

/**
 * Table Header Cell Component
 *
 * This component is a table header cell (`<th>`) with sorting capabilities.
 * When `sortField` and `onSort` are provided, it renders a sortable header.
 *
 * @example
 * ```gts
 * <THeadTh @sortField="name" @activeSortField={{this.activeSortField}} @onSort={{this.sort}}>
 *   Name
 * </THeadTh>
 * ```
 */
const THeadTh: TOC<THeadThSignature> = <template>
  {{#if (and @sortField @onSort)}}
    <TableSortableHeader
      {{! @glint-expect-error: glint can't infer the type is checked because of 'and' helper }}
      @tableSort={{@activeSortField}}
      {{! @glint-expect-error: glint can't infer the type is checked because of 'and' helper }}
      @sortField={{@sortField}}
      {{! @glint-expect-error: glint can't infer the type is checked because of 'and' helper }}
      @onSort={{@onSort}}
      @sortFormat={{@sortFormat}}
      ...attributes
    >
      {{yield}}
    </TableSortableHeader>
  {{else}}
    <th data-table-head-cell ...attributes>
      {{yield}}
    </th>
  {{/if}}
</template>;

/**
 * Configuration options for table header components.
 *
 * Typography, color, padding, and borders are owned by the .app-table
 * semantic class in addons/tio-ui/src/styles.css. Consumers don't need
 * to splat a cellClass — the parent <table class="app-table"> handles
 * th/td styling via descendant selectors.
 */
const THeadOptions = {
  rowClass: '',
  cellClass: '',
  tr: THeadTr,
  th: THeadTh,
};

/**
 * Signature for the TBodyTr component.
 * This component represents a table body row (`<tr>`) with specific styling.
 *
 * @block default - The default block for yielding content inside the row.
 */
interface TBodyTrSignature {
  Blocks: {
    default: [];
  };
  Element: HTMLTableRowElement;
}

/**
 * Table Body Row Component
 *
 * This component is a table body row (`<tr>`) with specific styling.
 *
 * @example
 * ```gts
 * <TBodyTr>
 *   <td>Data 1</td>
 *   <td>Data 2</td>
 * </TBodyTr>
 * ```
 */
const TBodyTr: TOC<TBodyTrSignature> = <template>
  <tr ...attributes>
    {{yield}}
  </tr>
</template>;

/**
 * Signature for the TBodyTd component.
 * This component represents a table body cell (`<td>`) with specific styling.
 *
 * @block default - The default block for yielding content inside the cell.
 */
interface TBodyTdSignature {
  Args: {
    stickyHeader?: boolean;
  };
  Blocks: {
    default: [];
  };
  Element: HTMLTableCellElement;
}

/**
 * Table Body Cell Component
 *
 * This component is a table body cell (`<td>`) with specific styling.
 *
 * @example
 * ```gts
 * <TBodyTd>
 *   Data
 * </TBodyTd>
 * ```
 */
const TBodyTd: TOC<TBodyTdSignature> = <template>
  <td data-table-body-cell ...attributes>
    {{yield}}
  </td>
</template>;

/**
 * Configuration options for table body components.
 *
 * Cell styling lives in the .app-table semantic class (see
 * addons/tio-ui/src/styles.css). The empty cellClass here is preserved
 * for backward compatibility with consumers that still splat
 * options.cellClass onto their custom row components — the splat is
 * a no-op now, harmless until those call sites migrate.
 */
export const TBodyOptions = {
  rowClass: '',
  cellClass: '',
  tr: TBodyTr,
  td: TBodyTd,
};

/**
 * Signature for the TFootTr component.
 * This component represents a table footer row (`<tr>`) with specific styling.
 *
 * @arg {boolean} [sticky] - If true, the footer row will be sticky at the bottom.
 *
 * @block default - The default block for yielding content inside the row.
 */
interface TFootTrSignature {
  Args: {
    sticky?: boolean;
  };
  Blocks: {
    default: [];
  };
  Element: HTMLTableRowElement;
}

/**
 * Table Footer Row Component
 *
 * This component is a table footer row (`<tr>`) with specific styling.
 * When the `sticky` argument is true, the row will be sticky at the bottom.
 *
 * @example
 * ```gts
 * <TFootTr @sticky={{true}}>
 *   <td>Footer Data</td>
 * </TFootTr>
 * ```
 */
const TFootTr: TOC<TFootTrSignature> = <template>
  <tr class={{if @sticky "sticky bottom-0 z-5"}} ...attributes>
    {{yield}}
  </tr>
</template>;

/**
 * Signature for the TFootTd component.
 * This component represents a table footer cell (`<td>`) with specific styling.
 *
 * @block default - The default block for yielding content inside the cell.
 */
interface TFootTdSignature {
  Blocks: {
    default: [];
  };
  Element: HTMLTableCellElement;
}

const TFootTd: TOC<TFootTdSignature> = <template>
  <td ...attributes>
    {{yield}}
  </td>
</template>;

/**
 * Configuration options for table footer components.
 */
const TFootOptions = {
  rowClass: '',
  cellClass: '',
  tr: TFootTr,
  td: TFootTd,
};

/**
 * Signature for the Table component.
 * This component represents an entire table with configurable header, body, and footer.
 *
 * @arg {boolean} isLoading - Indicates if the table is in a loading state.
 * @arg {number} [previewRowLimit] - The number of preview rows to display when loading.
 * @arg {number} [previewColspan] - The colspan value for the preview cells.
 *
 * @block thead - Yields the THeadOptions for configuring the table header.
 * @block tbody - Yields the TBodyOptions for configuring the table body.
 * @block tfoot - Yields the TFootOptions for configuring the table footer.
 */
interface TableSignature {
  Args: {
    isLoading?: boolean;
    previewRowLimit?: number;
    previewColspan?: number;
    stickyHeader?: boolean;
    headerClass?: string;
    bodyClass?: string;
    surface?: boolean;
  };
  Blocks: {
    // yields tr class, th class
    thead: [typeof THeadOptions];
    tbody: [typeof TBodyOptions];
    tfoot: [typeof TFootOptions];
  };
  Element: HTMLTableElement;
}

/**
 * Table Component
 *
 * This component represents an entire table with a header, body, and footer.
 * It supports loading states and allows customization of header, body, and footer through blocks.
 *
 * @example
 * ```gts
 * <Table @isLoading={{this.isLoading}}>
 *   <:thead as |headOptions|>
 *     <headOptions.tr>
 *       <headOptions.th @sortField="name" @activeSortField={{this.activeSortField}} @onSort={{this.sort}}>
 *         Name
 *       </headOptions.th>
 *     </headOptions.tr>
 *   </:thead>
 *   <:tbody as |bodyOptions|>
 *     <bodyOptions.tr>
 *       <bodyOptions.td>
 *         Data 1
 *       </bodyOptions.td>
 *       <bodyOptions.td>
 *         Data 2
 *       </bodyOptions.td>
 *     </bodyOptions.tr>
 *   </:tbody>
 *   <:tfoot as |footOptions|>
 *     <footOptions.tr @sticky={{true}}>
 *       <footOptions.td>
 *         Footer Data
 *       </footOptions.td>
 *     </footOptions.tr>
 *   </:tfoot>
 * </Table>
 * ```
 */
const Table: TOC<TableSignature> = <template>
  {{! @surface uses tio-border-default (not bare `border-default`) — the
      legacy `.border-default` atomic in utilities.css resolves to the dark
      HeroUI default-intent color (#474747) and shadows the semantic class,
      rendering a near-black border. }}
  <table
    data-table
    class="app-table
      {{if @stickyHeader 'app-table--sticky'}}
      {{if @surface 'rounded-lg border tio-border-default tio-bg-surface shadow-sm'}}"
    ...attributes
  >
    <thead class={{@headerClass}}>
      {{yield THeadOptions to="thead"}}
    </thead>
    <tbody class={{@bodyClass}}>
      {{#if @isLoading}}
        {{#each (createSkeletonArray (or @previewRowLimit 3))}}
          <tr>
            <td class="p-2 animate-pulse" colspan="{{or @previewColspan 100}}">
              <div data-table-skeleton-bar class="app-table-skeleton-bar"></div>
            </td>
          </tr>
        {{/each}}
      {{else}}
        {{yield TBodyOptions to="tbody"}}
      {{/if}}
    </tbody>
    <tfoot>
      {{yield TFootOptions to="tfoot"}}
    </tfoot>
  </table>
</template>;

export default Table;
